// src/components/Common/EmptyStateHero.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom'; // <--- Inyectamos el cerebro de navegación
import { Box, Typography, Button, Paper, Container, Stack } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CableIcon from '@mui/icons-material/Cable'; 

interface EmptyStateHeroProps {
  title?: string; // Opcionales porque pondremos defaults inteligentes
  description?: string;
}

const EmptyStateHero: React.FC<EmptyStateHeroProps> = ({ 
  title = "Motor de Inteligencia en Espera", 
  description = "Aún no hemos detectado datos suficientes. Conecta tus fuentes o carga tu histórico transaccional para activar la red neuronal." 
}) => {
  const navigate = useNavigate(); // Hook de navegación activo

  // RUTAS CENTRALIZADAS (Single Source of Truth)
  const handlePrimaryAction = () => navigate('/data-upload');
  const handleSecondaryAction = () => navigate('/connections');

  return (
    <Container 
        maxWidth="lg" 
        sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            alignItems: 'center',
            minHeight: '80vh' // Geometría forzada para empujar footer
        }}
    >
      <Paper 
        elevation={0} 
        sx={{ 
            p: 5, 
            maxWidth: '600px',
            textAlign: 'center',
            background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.6) 0%, rgba(30, 41, 59, 0.6) 100%)', 
            border: '1px solid rgba(51, 65, 85, 0.5)', 
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
            backdropFilter: 'blur(10px)' 
        }}
      >
        <AutoAwesomeIcon sx={{ 
            position: 'absolute', 
            top: -20, right: -20, fontSize: 150, 
            color: '#fcd34d', opacity: 0.02 
        }} />

        <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ 
                width: 60, height: 60, borderRadius: '50%', 
                bgcolor: 'rgba(56, 189, 248, 0.1)', 
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                mx: 'auto', mb: 3
            }}>
                <CloudUploadIcon sx={{ fontSize: 30, color: '#38bdf8' }} />
            </Box>
            
            <Typography variant="h5" fontWeight="700" gutterBottom sx={{ color: '#f8fafc' }}>
                {title}
            </Typography>
            
            <Typography variant="body2" sx={{ color: '#94a3b8', mb: 5, lineHeight: 1.6 }}>
                {description}
            </Typography>

            {/* STACK DE ACCIONES AUTOMATIZADAS */}
            <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                justifyContent="center"
                alignItems="center"
            >
                {/* ACCIÓN PRIMARIA: CARGA MANUAL */}
                <Button 
                    variant="contained" 
                    size="medium"
                    startIcon={<CloudUploadIcon />}
                    onClick={handlePrimaryAction}
                    sx={{ 
                        py: 1, px: 3,
                        width: { xs: '100%', sm: 'auto' },
                        borderRadius: 2,
                        fontSize: '0.95rem',
                        bgcolor: '#38bdf8', color: '#0f172a',
                        fontWeight: '600', textTransform: 'none',
                        boxShadow: '0 4px 12px rgba(56, 189, 248, 0.2)',
                        '&:hover': { bgcolor: '#0ea5e9' }
                    }}
                >
                    Cargar Datos
                </Button>

                {/* ACCIÓN SECUNDARIA: CONEXIONES */}
                <Button 
                    variant="outlined" 
                    size="medium"
                    startIcon={<CableIcon />}
                    onClick={handleSecondaryAction}
                    sx={{ 
                        py: 1, px: 3,
                        width: { xs: '100%', sm: 'auto' },
                        borderRadius: 2,
                        fontSize: '0.95rem',
                        borderColor: '#475569', color: '#cbd5e1',
                        fontWeight: '600', textTransform: 'none',
                        borderWidth: '1px',
                        '&:hover': { 
                            borderColor: '#94a3b8',
                            bgcolor: 'rgba(148, 163, 184, 0.05)',
                            color: '#f8fafc',
                            borderWidth: '1px'
                        }
                    }}
                >
                    Conectar Fuente
                </Button>
            </Stack>
        </Box>
      </Paper>
    </Container>
  );
};

export default EmptyStateHero;